import { EmptyState } from '@/components/empty-state';
import { defaultRule } from '@/components/segments/rule-builder';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useCreateSegment, useDeleteSegment, useSegments, type Segment } from '@/lib/queries';
import { useContextStore } from '@/stores/context';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Trash2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_authed/segments/')({ component: SegmentsPage });

// The API returns these; `Segment` in queries.ts is the narrow contract the mutations need.
type SegmentRow = Segment & { updated_at?: string };

const NO_AUDIENCE_SIZE =
  'There is no "this matches N users" preview, here or anywhere in v1. Trait values are stored as ' +
  'AES-GCM encrypted blobs, so the server cannot query them — an audience count would require a ' +
  'crypto design we have deliberately not made. Segments are evaluated per user, at delivery.';

function SegmentsPage(): React.JSX.Element {
  const appId = useContextStore((s) => s.appId);
  const navigate = useNavigate();
  const { data: segments, isLoading } = useSegments(appId);
  const create = useCreateSegment(appId);
  const remove = useDeleteSegment(appId);

  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [pendingDelete, setPendingDelete] = useState<Segment | null>(null);

  const onCreate = (): void => {
    create.mutate(
      { name: name.trim(), definition: defaultRule() },
      {
        onSuccess: (segment) => {
          setOpen(false);
          setName('');
          void navigate({ to: '/segments/$id', params: { id: segment.id } });
        },
        onError: (error) => toast.error(error.message),
      }
    );
  };

  const onDelete = (segment: Segment): void => {
    remove.mutate(segment.id, {
      // A segment a campaign still points at comes back 409 with a message that says exactly that.
      // Showing "Something went wrong" instead would hide the one thing the author can act on.
      onError: (error) => toast.error(error.message),
      onSuccess: () => toast.success(`Deleted “${segment.name}”`),
      onSettled: () => setPendingDelete(null),
    });
  };

  const newSegmentDialog = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>New segment</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New segment</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="segment-name">Name</Label>
          <Input
            id="segment-name"
            value={name}
            placeholder="Paying users"
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button onClick={onCreate} disabled={name.trim() === '' || create.isPending}>
            Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold tracking-tight">Segments</h1>
        {segments && segments.length > 0 && newSegmentDialog}
      </div>

      {isLoading && <Skeleton className="h-40 w-full" />}

      {segments && segments.length === 0 && (
        <EmptyState
          title="No segments yet"
          description="A segment is an and/or tree of trait and event conditions. Campaigns target one."
          action={newSegmentDialog}
          note={NO_AUDIENCE_SIZE}
        />
      )}

      {segments && segments.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead className="w-12" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {(segments as SegmentRow[]).map((segment) => (
              <TableRow key={segment.id}>
                <TableCell>
                  <Link
                    to="/segments/$id"
                    params={{ id: segment.id }}
                    className="font-medium hover:underline"
                  >
                    {segment.name}
                  </Link>
                </TableCell>
                <TableCell className="text-muted-foreground">
                  {segment.updated_at ? new Date(segment.updated_at).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${segment.name}`}
                    onClick={() => setPendingDelete(segment)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <AlertDialog open={pendingDelete !== null} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete “{pendingDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>
              This cannot be undone. A segment a campaign still targets cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => pendingDelete && onDelete(pendingDelete)}
              disabled={remove.isPending}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
